/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Button } from '@mantine/core';
import RecordPaymentModal from '../financials/RecordPaymentModal';
import ViewRegistrationFormModal from '../Forms/ViewRegistrationFormModal';
import { useModalState } from '../../../helpers/hooks';
import { triggerNotification } from '../../../helpers/notificationHelper';
import AppText from '../../common/AppText';
import classes from '../../../styles/nestedStyles.module.css';
import { isRegistrationUnavailable } from '../../../helpers/shop/webstoreHelper';
import WsCheckoutLineItem from '../ecommerce/webstore/sections/checkoutContent/WsCheckoutLineItem';

const CheckoutRegistrationLineItem = ({
  lineItem,
  regCart,
  onCustomize,
  onUpdateCartProducts,
  onRemoveCartProducts,
  onRemoveUnavailableProducts,
  onRedeemCoupon,
  submitting,
  isOrderHistory
}) => {
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();

  const cartProduct = regCart.regCartProducts.find(
    (p) =>
      p.regFormSubmission.pkRegFormSubmission === lineItem.entityId &&
      p.regProduct.pkRegProduct ===
        p.regFormSubmission.regAssociationDivisionForm.fkRegProduct
  );

  const latestVersionRegAssociationDivisionForm = regCart.latestVersionRegAssociationDivisionForms?.find(
    (p) =>
      p.regAssociationDivision.pkRegAssociationDivision ===
        cartProduct.regFormSubmission.regAssociationDivisionForm
          .regAssociationDivision.pkRegAssociationDivision &&
      p.regForm.uuid ===
        cartProduct.regFormSubmission.regAssociationDivisionForm.regForm.uuid
  );
  const hasProductChanged = !!latestVersionRegAssociationDivisionForm;
  const isProductUnavailable =
    latestVersionRegAssociationDivisionForm &&
    isRegistrationUnavailable(latestVersionRegAssociationDivisionForm);

  const waitlistReservation = cartProduct?.regWaitlistReservations
    .filter((r) => !r.deleted)
    .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate))[0];

  const openRegFormModal = () => {
    onOpenModal('viewRegForm', null);
  };

  const onError = (errorMessage) => {
    triggerNotification(errorMessage);
    onOpenModal('viewRegForm', errorMessage);
  };

  const updateCartProduct = (formData) => {
    onUpdateCartProducts(
      {
        regCartProducts: [
          {
            pkRegCartProduct: cartProduct.pkRegCartProduct,
            count: cartProduct.count,
            regFormSubmissionAnswers: formData.regFormSubmissionAnswers,
            divisionRoles: formData.divisionRoles,
            financialAssistanceId: formData.isFinancialAssistanceEnabled
              ? formData.financialAssistanceId
              : null,
            regFormSubmissionExternalIds: formData.regFormSubmissionExternalIds,
            regFormSubmissionPersonContacts:
              formData.regFormSubmissionPersonContacts
          }
        ]
      },
      () => {
        triggerNotification('Registration form updated!', 'Success', 'green');
        onCloseModal();
      },
      onError
    );
  };

  const onRemove = () => {
    onRemoveCartProducts({ pkRegCartProducts: [cartProduct.pkRegCartProduct] });
  };

  return (
    <>
      <RecordPaymentModal
        isOpen={modalState.isOpen && modalState.action === 'recordPayment'}
        onClose={onCloseModal}
        regAssociation={cartProduct.regProduct.regAssociation}
        regFormSubmission={cartProduct.regFormSubmission}
      />
      <ViewRegistrationFormModal
        error={
          modalState.isOpen && modalState.action === 'viewRegForm'
            ? modalState.item
            : ''
        }
        isDisabled={isOrderHistory}
        isLoading={modalState.loading}
        isOpen={modalState.isOpen && modalState.action === 'viewRegForm'}
        merchPackageRegAssociation={regCart.regAssociation}
        onClose={onCloseModal}
        onSubmit={(formData) => {
          onChangeModalLoading(true);
          updateCartProduct(formData);
        }}
        pkRegFormSubmission={cartProduct.regFormSubmission?.pkRegFormSubmission}
        showMerchPackageShowcase={!isOrderHistory}
      />
      <WsCheckoutLineItem
        enableImageNotAvailablePlaceholder={false}
        expiryDate={
          waitlistReservation?.expiryDate ||
          cartProduct.regFormSubmission.regAssociationDivisionForm.regForm
            .endDate
        }
        footerButton={
          isOrderHistory ? (
            <Button
              color="#FFF"
              disabled={submitting || hasProductChanged}
              onClick={openRegFormModal}
              radius="xs"
              size="lg"
              style={{ width: 200 }}
              variant="outline"
            >
              View Form
            </Button>
          ) : cartProduct.regFormSubmission.isCompleted ? (
            <Button
              color="blue"
              disabled={submitting || hasProductChanged}
              onClick={openRegFormModal}
              radius="xs"
              size="lg"
              style={{ width: 200 }}
              variant="filled"
            >
              Update Form
            </Button>
          ) : (
            <Button
              className={
                !submitting && !hasProductChanged
                  ? classes['pulse-red-border']
                  : null
              }
              color="#FFF"
              disabled={submitting || hasProductChanged}
              onClick={openRegFormModal}
              radius="xs"
              size="lg"
              style={{ width: 200 }}
              variant="outline"
            >
              Fill out form
            </Button>
          )
        }
        footerSubTitle={`${cartProduct.regFormSubmission.regPerson.firstName} ${cartProduct.regFormSubmission.regPerson.lastName}`}
        footerTitle="Participant"
        hasProductChanged={hasProductChanged}
        isInvite={!!waitlistReservation}
        isOrderHistory={isOrderHistory}
        isProductUnavailable={isProductUnavailable}
        isSingleUnit
        lineItem={lineItem}
        onCustomize={onCustomize}
        onRedeemCoupon={(code) =>
          onRedeemCoupon({
            pkRegCartProduct: cartProduct.pkRegCartProduct,
            code
          })
        }
        onRemove={onRemove}
        onRemoveUnavailableProducts={onRemoveUnavailableProducts}
        productUrl={`/association/${cartProduct.regProduct.regAssociation.pkRegAssociation}`}
        showFooter
        submitting={submitting}
        subTitleContent={
          <>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              {
                cartProduct.regFormSubmission.regAssociationDivisionForm
                  .regAssociationDivision.name
              }{' '}
              &middot;{' '}
              {
                cartProduct.regFormSubmission.regAssociationDivisionForm
                  .regAssociationDivision.regAssociationSeason.name
              }
            </AppText>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Closes{' '}
              {dayjs(
                cartProduct.regFormSubmission.regAssociationDivisionForm.regForm
                  .endDate
              ).format('MMM D, YYYY')}
            </AppText>
          </>
        }
        titleOverride={
          cartProduct.regFormSubmission.regAssociationDivisionForm.titleOverride
            ? cartProduct.regFormSubmission.regAssociationDivisionForm
                .titleOverride
            : cartProduct.regFormSubmission.regAssociationDivisionForm.regForm
                .name
        }
      />
    </>
  );
};

CheckoutRegistrationLineItem.propTypes = {
  isOrderHistory: PropTypes.bool,
  lineItem: PropTypes.object,
  onCustomize: PropTypes.func,
  onRedeemCoupon: PropTypes.func,
  onRemoveCartProducts: PropTypes.func,
  onRemoveUnavailableProducts: PropTypes.func,
  onUpdateCartProducts: PropTypes.func,
  regCart: PropTypes.object,
  submitting: PropTypes.bool
};

export default CheckoutRegistrationLineItem;
